import { mapState as rootMapState } from 'pinia';
import { mapState, mapActions } from '@/store/helpers';
import { usePageStore, useUserStore } from '@/store';
import { Swiper, SwiperSlide, fixSwiperOptions } from '@/common/swiper';
import LeaveMsg from '@/components/sign-log/leave-msg/index';
import util from '@/common/util';
import { dateFormat } from '@/common/format';
import { fifteenMin } from './schedule-appointment.js';

export const scheduleAppointment = {
    i18next: { ns: 'md-detail-info' },
    data() {
        return {
            loading: false
        };
    },
    inject: ['styleTheme'],
    props: {
        apptTitle: { type: String, default: '' },
        apptSubTitle: { type: String, default: '' },
        apptSubmitText: { type: String, default: '' },
        apptQuestionMsg: { type: String, default: '' },
        isBannerSchedule: { type: Boolean, default: false }
    },
    computed: {
        ...rootMapState(useUserStore, {
            user: store => store.user
        }),
        ...rootMapState(usePageStore, {
            monthSimpleNames: store => store.monthSimpleNames
        }),
        ...mapState({
            statusStore: store => store.scheduleTour,
            showVirtualTour: store => store.showVirtualTour,
            scheduleNameFormat: store => store.scheduleNameFormat,
            schedulePhoneRequired: store => store.schedulePhoneRequired,
            listingDetail: store => store.listingDetail
        }),
        isMobileSize() {
            return this.platform.isMobile;
        },
        selectedDateIndex() {
            return this.statusStore.selectedDateIndex;
        },
        tourTypeIdx() {
            return this.statusStore.tourTypeIdx;
        },
        requestType() {
            return this.tourTypeIdx == 1 ? 'virtual_tour' : 'tour_info';
        },
        scheduleBtnClassForGA() {
            return this.tourTypeIdx == 1 ? 'virtual-tour' : 'person-tour';
        },
        tourType() {
            let showVT = this.showVirtualTour == true,
                tabs = showVT
                    ? [
                        this.$t('md-detail-info.inperson'),
                        this.$t('md-detail-info.virtualTour')
                    ]
                    : [this.$t('md-detail-info.inperson')];
            return {
                title: this.$t('md-detail-info.requestATour'),
                show: showVT,
                tip: this.$t('md-detail-info.ifYouWouldLikeTo'),
                tabs
            };
        }
    },
    methods: {
        ...mapActions(['updateScheduleTour']),
        selectTourType(idx) {
            this.updateScheduleTour({ tourTypeIdx: idx });
        },
        async recordVisitor() {
            if (this.loading) {
                return; 
            }
            this.loading = true;
            let { info } = this.listingDetail;
            this.$EventBus.$emit('siteTrack', {
                listingId: info.id,
                trackType: 'schedule_a_tour',
                address: info?.address,
                listingUrl: info?.detailUrl ?? info?.detailLink,
                listingType: 1 // 1 - active listing, 0 - sold listing
            });
            try {
                let { start: startTime, duration } = this.timeRange;
                let endTime = startTime + duration * fifteenMin;
                await this.axiosIns.post('/api-site/record/visitorInfo', {
                    fullName: this.user.fullName,
                    phoneNum: this.user.phoneNum,
                    account: this.user.account,
                    recordFlag: this.requestType,
                    emailAddress: this.user.emailAddress || this.user.account,
                    appointmentStartTime: startTime,
                    appointmentEndTime: endTime,
                    listingId: info.id,
                    question: '',
                    timeZone: this.getCookie('timezone') || util.getTimeZone()
                });
                let appointTime = {
                    date: dateFormat(
                        'MMMM dd, yyyy',
                        new Date(startTime),
                        this.$pinia
                    ),
                    duration: `${dateFormat(
                        'hh:mmp',
                        new Date(startTime),
                        this.$pinia
                    )} - ${dateFormat(
                        'hh:mmp',
                        new Date(endTime),
                        this.$pinia
                    )}`
                };
                document
                    .querySelector('.detail-schedule-container')
                    .scrollIntoView();
                this.$emit('onSuccess', {
                    appointTime,
                    appointmentStartTime: startTime,
                    appointmentEndTime: endTime
                });
            } catch (error) {
                console.log(error);
            } finally {
                this.loading = false;
            }
        },
        submitForm(theme = 'standard', leaveMsgOptions) {
            if (this.user) {
                this.recordVisitor();
            } else {
                let { streetAddress, cityAddress } = this.listingDetail.info;
                let adds = [streetAddress, cityAddress];
                let address = adds.filter(v => v).join(', ');
                let options = Object.assign(
                    {
                        popAnimation: this.isMobileSize
                            ? 'bottomToUp'
                            : 'scale',
                        popCustomClass: 'standard',
                        onsuccess: () => {
                            setTimeout(() => {
                                this.recordVisitor();
                            }, 0);
                        },
                        msgTitle:
                            this.apptTitle ||
                            this.$t('md-detail-info.wereExcitedToShow'),
                        msgSubTitle:
                            this.apptSubTitle ||
                            this.$t('md-detail-info.justAddAFewMoreDetails'),
                        msgSubmitText: this.apptSubmitText,
                        questionMsg:
                            this.apptQuestionMsg ||
                            this.$t('md-detail-info.iWouldLikeToSchedule', {
                                address: address.replace(/, */g, ', ') || ''
                            }),
                        nameFormat: this.scheduleNameFormat,
                        ignorPhone: !this.schedulePhoneRequired,
                        gaPopScenario: 'Request Showing'
                    },
                    leaveMsgOptions
                );
                LeaveMsg.open(options, theme, 'hideOtherPop');
            }
        }
    }
};

export const agent = {
    computed: {
        ...rootMapState(usePageStore, {
            page: store => store.page || {},
            company: store => store.companyInfo || {},
            monthSimpleNames: store => store.monthSimpleNames
        }),
        ...mapState({
            _agentInfo: store => store.listingDetail.agentInfo,
            _coAgent: store => store.listingDetail.coAgentInfo,
            _additionalAgentInfos: store => store.listingDetail.additionalAgentInfos,
            showCoAgent: store => store.showCoAgent,
        }),
        agentInfo({ _agentInfo: agent, company }) {
            if (company && company.name && agent) {
                if (this.isSubdomain && this.isParentWebsiteOfficeOrTeam) {
                    agent.company = this?.siteOwnerInfo?.officeName;
                } else {
                    agent.company = company.name;
                }
            }
            return agent;
        },
        coAgentInfo({ _coAgent, _additionalAgentInfos, company }) {
            if (!this.showCoAgent) {
                return [];
            }
            let coAgentArr = [];
            if (_coAgent) {
                coAgentArr.push(_coAgent);
            }
            if (_additionalAgentInfos?.length > 0) {
                coAgentArr.push(..._additionalAgentInfos);
            }
            if (coAgentArr.length === 0) {
                return [];
            }

            return coAgentArr.map(item => {
                let companyName = "";
                if (this.isSubdomain && this.isParentWebsiteOfficeOrTeam) {
                    companyName = this?.siteOwnerInfo?.officeName;
                } else {
                    companyName = company?.name;
                }
                return {
                    ...item,
                    company: companyName
                };
            });
        },
        mlsShowAgent() {
            // 1  is displayed ， 0  hide
            let mlsOrgs = this.page.mlsOrgs || [];
            let mlsOrg = mlsOrgs[0] || {};
            return mlsOrg.showAgentBlock === 1;
        },
        isSubdomain() {
            return this.page?.websiteInfo?.siteType === 3 && this.page?.websiteInfo?.siteLevel === 0;
        },
        siteId({ page }) {
            return page.siteId;
        },
        isParentWebsiteOfficeOrTeam({ page }) {
            // TODO: office site: site_level = 2 ; team site: site_level = 0 and site_type = 1
            // console.log('parentSiteInfo', page?.parentSiteInfo);
            const isOfficeSite = page?.parentSiteInfo?.siteLevel === 2;
            const isTeamSite = page?.parentSiteInfo?.siteLevel === 0 && page?.parentSiteInfo?.siteType === 1;
            return isOfficeSite || isTeamSite;
        }
    },
};

export const schedule = {
    emits: ['onPopRegister'],
    i18next: { ns: 'md-detail-info' },
    inject: ['styleTheme'],
    data() {
        return {
            siteOwnerInfo: {} //  Current station information ，teamId, officeName, officeId, siteOwnerName information         
        };
    },
    props: {
        inPop: { type: Boolean, default: false },
        swiperConfig: { type: Object, default: () => ({}) },
        apptTitle: { type: String, default: '' },
        apptSubTitle: { type: String, default: '' },
        apptSubmitText: { type: String, default: '' },
        apptQuestionMsg: { type: String, default: '' },
        additionalTitle: String,
        isBannerSchedule: { type: Boolean, default: false }
    },
    computed: {
        ...mapState({
            showMakeOffer: store => store.showMakeOffer,
            statusStore: store => store.scheduleTour,
            agentInfo: store => store.agentInfo
        }),
        success() {
            return this.statusStore.success;
        },
        ...agent.computed
    },
    methods: {
        ...mapActions(['updateScheduleTour']),
        onPopRegister() {
            this.$emit('onPopRegister');
        },
        onSuccess(params) {
            if (this.inPop) {
                this.$emit('onSuccess', params);
            } else {
                this.updateScheduleTour({
                    success: true,
                    ...params
                });
            }
        },
        getOfficeName() {
            this.axiosIns
                .get('/api-site/office/getPocketOfficeInfo', {
                    params: { siteId: this.siteId }
                })
                .then(res => {
                    console.log('getOfficeName res', res.data);
                    this.siteOwnerInfo = res.data || {};
                });
        }
    },
    beforeMount() {
        console.log('mounted siteId', this.siteId);
        this.getOfficeName();
    }
};

export const swiperMixin = {
    data: function() {
        return {
            space: 10
        };
    },
    components: {
        Swiper,
        Slide: SwiperSlide
    },
    computed: {
        swiperOption({ space }) {
            let options = {
                slidesPerView: 4,
                spaceBetween: space,
                slidesPerGroup: 4,
                navigation: {
                    closest: '.date-container',
                    prevEl: '.schedule-appointment-prev',
                    nextEl: '.schedule-appointment-next',
                    disabledClass: 'disabled'
                }
            };
            options = fixSwiperOptions(options);
            return options;
        }
    }
};

export default {
    scheduleAppointment,
    schedule,
    agent,
    swiperMixin
};
